import React, { useState, useEffect } from 'react';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaClock, FaFacebookF, FaInstagram, FaLinkedinIn, FaGithub, FaPaperPlane } from 'react-icons/fa';
import { motion } from 'framer-motion';
import AOS from 'aos';
import 'aos/dist/aos.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4200';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "", email: "", contact: "", address: "", subject: "", message: ""
  });
  const [contactDetails, setContactDetails] = useState({
    email: "", contact: "", address: "", openHour: "",
    socialMedia: { facebook: "", instagram: "", linkedin: "", github: "" }
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });

    // Fetch Contact Details (Public)
    fetch(`${API_URL}/admin/contact-details`) // Using admin route but it should be public or we need a public endpoint. 
    // Wait, previous file used /public/contact-details but admin router had /contact-details under admin. 
    // Let's use the one that works in ManageContact first, but wait, frontend needs public access.
    // Checking previous AdminRouter... actually it has: Router.get("/contact-details", ... GetContactDetails) under /admin prefix.
    // AND Router.get("/public/contact-details", GetContactDetails) under public prefix.
    // So we should use /public/contact-details.

    fetch(`${API_URL}/admin/public/contact-details`) // Prefixed with /admin because of app.use('/admin', AdminRouter) in server.
      .then(res => res.json())
      .then(data => {
        if (data.success) setContactDetails(data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching contact details:", err);
        setLoading(false);
      });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim() || formData.name.length < 2) newErrors.name = "Name is required";
    if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = "Valid email is required";
    if (!formData.subject.trim() || formData.subject.length < 3) newErrors.subject = "Subject is required";
    if (!formData.message.trim() || formData.message.length < 10) newErrors.message = "Message must be at least 10 characters";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/admin/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await res.json();

      if (data.success) {
        setSuccess(true);
        setFormData({ name: "", email: "", contact: "", address: "", subject: "", message: "" });
        setTimeout(() => setSuccess(false), 5000);
      } else {
        alert(data.message || 'Failed to send message');
      }
    } catch (error) {
      alert('Failed to send. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const infoItems = [
    { icon: FaMapMarkerAlt, title: 'Visit Us', value: contactDetails.address || 'Address not available', color: 'text-rose-500', bg: 'bg-rose-50' },
    { icon: FaPhoneAlt, title: 'Call Us', value: contactDetails.contact || 'Phone not available', link: `tel:${contactDetails.contact}`, color: 'text-emerald-500', bg: 'bg-emerald-50' },
    { icon: FaEnvelope, title: 'Email Us', value: contactDetails.email || 'Email not available', link: `mailto:${contactDetails.email}`, color: 'text-blue-500', bg: 'bg-blue-50' },
    { icon: FaClock, title: 'Working Hours', value: contactDetails.openHour || 'Mon-Sat: 9AM - 6PM', color: 'text-amber-500', bg: 'bg-amber-50' }
  ];

  const socialLinks = [
    { icon: FaFacebookF, url: contactDetails.socialMedia?.facebook, color: "hover:bg-[#1877F2]" },
    { icon: FaInstagram, url: contactDetails.socialMedia?.instagram, color: "hover:bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7]" },
    { icon: FaLinkedinIn, url: contactDetails.socialMedia?.linkedin, color: "hover:bg-[#0A66C2]" },
    { icon: FaGithub, url: contactDetails.socialMedia?.github, color: "hover:bg-[#333]" }
  ].filter(s => s.url);

  return (
    <div className="font-sans text-slate-800 bg-slate-50 min-h-screen">

      {/* HERO SECTION */}
      <section className="relative bg-slate-900 pt-32 pb-48 text-center px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10"></div>
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-indigo-500/20 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-fuchsia-500/20 rounded-full blur-[120px]"></div>

        <div className="relative z-10 max-w-4xl mx-auto" data-aos="fade-up">
          <span className="inline-block py-1 px-3 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 font-bold text-xs mb-6 uppercase tracking-widest">
            Get In Touch
          </span>
          <h1 className="text-5xl md:text-7xl font-black text-white mb-8 leading-tight">
            Let's Start a <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-fuchsia-400">Conversation</span>
          </h1>
          <p className="text-xl text-slate-400 font-light leading-relaxed max-w-2xl mx-auto">
            Have a project in mind or just want to verify us? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* INFO CARDS - Floating overlap */}
      <section className="container mx-auto px-6 -mt-24 relative z-20 mb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {infoItems.map((item, i) => (
            <div key={i} className="bg-white p-8 rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 hover:transform hover:-translate-y-2 transition-all duration-300 group">
              <div className={`w-14 h-14 rounded-2xl ${item.bg} ${item.color} flex items-center justify-center text-2xl mb-6 group-hover:scale-110 transition-transform`}>
                <item.icon />
              </div>
              <h3 className="font-bold text-lg text-slate-800 mb-2">{item.title}</h3>
              {item.link ? (
                <a href={item.link} className="text-slate-500 hover:text-indigo-600 font-medium transition-colors break-words">{item.value}</a>
              ) : (
                <p className="text-slate-500 font-medium break-words">{item.value}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* MAIN CONTENT GRID */}
      <section className="container mx-auto px-6 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* Left: Content & Socials */}
          <div data-aos="fade-right">
            <h2 className="text-4xl font-extrabold text-slate-800 mb-6">Reach out to us directly.</h2>
            <p className="text-lg text-slate-600 mb-12 leading-relaxed">
              We are available on multiple platforms. Check out our social media channels or drop by our office for a coffee and a chat about your next big idea.
            </p>

            <div className="bg-white p-8 rounded-3xl shadow-lg border border-slate-100 mb-12">
              <h4 className="font-bold text-slate-800 mb-6">Connect across platforms</h4>
              <div className="flex flex-wrap gap-4">
                {socialLinks.map((social, i) => (
                  <a
                    key={i}
                    href={social.url}
                    target="_blank"
                    rel="noreferrer"
                    className={`w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 text-2xl transition-all duration-300 hover:text-white hover:shadow-lg hover:shadow-indigo-500/30 hover:-translate-y-1 ${social.color}`}
                  >
                    <social.icon />
                  </a>
                ))}
              </div>
            </div>

            {/* Map Placeholder or Decoration */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl h-[300px] border-4 border-white">
              <iframe
                title="Map"
                width="100%"
                height="100%"
                frameBorder="0"
                scrolling="no"
                marginHeight="0"
                marginWidth="0"
                src="https://maps.google.com/maps?width=100%25&amp;height=600&amp;hl=en&amp;q=1%20Grafton%20Street,%20Dublin,%20Ireland+(My%20Business%20Name)&amp;t=&amp;z=14&amp;ie=UTF8&amp;iwloc=B&amp;output=embed"
                style={{ filter: "grayscale(100%) contrast(1.2) opacity(0.7)" }}
              ></iframe>
              <div className="absolute inset-0 bg-indigo-900/10 pointer-events-none"></div>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="relative" data-aos="fade-left">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-fuchsia-600 transform rotate-2 rounded-[3rem] opacity-50 blur-xl"></div>
            <div className="relative bg-white p-8 md:p-12 rounded-[3rem] shadow-2xl border border-slate-100">
              <h3 className="text-2xl font-bold text-slate-800 mb-2">Send us a message</h3>
              <p className="text-slate-500 mb-8">Fill out the form below and we'll get back to you shortly.</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase ml-1">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full p-4 bg-slate-50 border ${errors.name ? 'border-rose-400 bg-rose-50' : 'border-slate-200'} rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold`}
                      placeholder="John Doe"
                    />
                    {errors.name && <p className="text-rose-500 text-xs ml-1">{errors.name}</p>}
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase ml-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full p-4 bg-slate-50 border ${errors.email ? 'border-rose-400 bg-rose-50' : 'border-slate-200'} rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold`}
                      placeholder="john@example.com"
                    />
                    {errors.email && <p className="text-rose-500 text-xs ml-1">{errors.email}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase ml-1">Phone (Optional)</label>
                    <input
                      type="text"
                      name="contact"
                      value={formData.contact}
                      onChange={handleChange}
                      className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-500 uppercase ml-1">Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className={`w-full p-4 bg-slate-50 border ${errors.subject ? 'border-rose-400 bg-rose-50' : 'border-slate-200'} rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold`}
                      placeholder="Project Inquiry"
                    />
                    {errors.subject && <p className="text-rose-500 text-xs ml-1">{errors.subject}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-500 uppercase ml-1">Message</label>
                  <textarea
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleChange}
                    className={`w-full p-4 bg-slate-50 border ${errors.message ? 'border-rose-400 bg-rose-50' : 'border-slate-200'} rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-semibold resize-none`}
                    placeholder="Tell us about your project..."
                  ></textarea>
                  {errors.message && <p className="text-rose-500 text-xs ml-1">{errors.message}</p>}
                </div>

                {success && (
                  <div className="p-4 bg-emerald-50 text-emerald-600 rounded-xl font-bold text-center border border-emerald-100 flex items-center justify-center gap-2">
                    <FaPaperPlane /> Message sent successfully!
                  </div>
                )}

                <button
                  disabled={isSubmitting}
                  className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-bold shadow-xl shadow-indigo-500/30 transform hover:-translate-y-1 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>Sending...</>
                  ) : (
                    <>Send Message <FaPaperPlane /></>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ContactSection;